import {
  ButtonLink,
  Card,
  Container,
  Eyebrow,
  Heading,
  Lead,
  Section,
} from '../components/ui';
import { usePageTitle } from '../lib/usePageTitle';

/** Spec 7: the capability list the homepage advertises. */
const CAPABILITIES = [
  {
    title: 'Acceptance-criteria validation',
    body: 'Every criterion in your spec gets a verdict. None is silently skipped.',
  },
  {
    title: 'Evidence collection',
    body: 'Screenshots, HTTP exchanges and console output, captured as the run happens.',
  },
  {
    title: 'Browser automation',
    body: 'Real Chromium in an isolated profile, driven only against origins you name.',
  },
  {
    title: 'Project-aware context',
    body: 'Your spec, architecture plan, run instructions and verification guide are the inputs.',
  },
  {
    title: 'Run history',
    body: 'Every run kept in its own timestamped folder. Nothing overwrites anything.',
  },
  {
    title: 'Multi-LLM support',
    body: 'Any OpenAI-compatible provider, including local models. No vendor lock-in.',
  },
  {
    title: 'Local execution',
    body: 'The agent runs on your machine. Project content stays there.',
  },
  {
    title: 'Secure API authentication',
    body: 'Client credentials exchanged for short-lived tokens, never sent per call.',
  },
  {
    title: 'Auditable reports',
    body: 'Markdown and JSON, each verdict citing the artefact that justifies it.',
  },
] as const;

/** Spec 7: the workflow diagram, as an ordered list. */
const WORKFLOW = [
  'Create account',
  'Choose subscription',
  'Create agent credentials',
  'Install WaveQA',
  'Configure project',
  'Provide QA documents',
  'Run verification',
  'Collect evidence',
  'Review QA report',
] as const;

/** Spec 7: the security section. */
const SECURITY = [
  'Encrypted credentials',
  'TLS-only communication',
  'No secrets in logs',
  'Local execution',
  'Permission boundaries',
  'Audit logging',
  'Rate limiting',
  'Subscription enforcement',
  'Credential rotation',
  'Secure browser execution',
  'Least privilege',
] as const;

export function Home() {
  usePageTitle('WaveQA — AI-Powered QA That Verifies What Matters');

  return (
    <>
      {/* --- Hero ------------------------------------------------------- */}
      <div className="relative overflow-hidden border-b border-hull-800">
        <div aria-hidden="true" className="bg-grid absolute inset-0 opacity-60" />
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-surge-500/60 to-transparent"
        />

        <Container className="relative py-20 sm:py-28">
          <div className="max-w-3xl">
            <Eyebrow>AI QA agent platform</Eyebrow>
            <Heading level={1} className="text-white">
              AI-Powered QA That <span className="text-gradient">Verifies What Matters</span>
            </Heading>
            <Lead className="mt-6 max-w-2xl">
              WaveQA helps engineering teams turn product specifications and acceptance
              criteria into repeatable, evidence-driven QA verification.
            </Lead>

            <div className="mt-9 flex flex-wrap gap-3">
              <ButtonLink to="/register">Get Started</ButtonLink>
              <ButtonLink to="/how-it-works" variant="secondary">
                See How WaveQA Works
              </ButtonLink>
            </div>

            <p className="mt-6 text-sm text-hull-400">
              Free plan includes 1 agent and 20 hours every month.
            </p>
          </div>
        </Container>
      </div>

      {/* --- The claim that distinguishes it ---------------------------- */}
      <Section className="border-b border-hull-800">
        <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
          <div>
            <Eyebrow>Why WaveQA</Eyebrow>
            <Heading className="text-white">A PASS you can audit</Heading>
            <Lead className="mt-5">
              Most AI tooling will happily tell you everything looks fine. WaveQA cannot:
              a criterion may only be marked <strong className="text-pass-400">PASS</strong> if
              the run captured an artefact proving it. Anything it could not verify is
              reported as <strong className="text-warn-400">BLOCKED</strong>, with the
              reason it could not be settled — never quietly counted as a success.
            </Lead>
            <Lead className="mt-4">
              That single rule is enforced in code, not in a prompt.
            </Lead>
          </div>

          <Card className="font-mono text-sm">
            <p className="mb-4 text-xs uppercase tracking-widest text-hull-400">
              acceptance-results.md
            </p>
            <ul className="space-y-2.5">
              <VerdictRow id="AC-1" status="PASS" detail="evidence/ac-01.png" />
              <VerdictRow id="AC-2" status="PASS" detail="evidence/ac-02-get.json" />
              <VerdictRow id="AC-3" status="FAIL" detail="expected copy absent" />
              <VerdictRow id="AC-4" status="BLOCKED" detail="environment unreachable" />
              <VerdictRow id="EC-1" status="BLOCKED" detail="needs a second account" />
            </ul>
            <p className="mt-5 border-t border-hull-700 pt-4 text-xs text-hull-400">
              Overall status: <span className="text-fail-400">FAILED</span> — one criterion
              failed, one could not be reached.
            </p>
          </Card>
        </div>
      </Section>

      {/* --- Capabilities ----------------------------------------------- */}
      <Section className="border-b border-hull-800">
        <Eyebrow>Capabilities</Eyebrow>
        <Heading className="text-white">Everything a verification run needs</Heading>

        <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CAPABILITIES.map((item) => (
            <Card as="li" key={item.title}>
              <h3 className="text-base font-semibold text-white">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-hull-300">{item.body}</p>
            </Card>
          ))}
        </ul>
      </Section>

      {/* --- Workflow ---------------------------------------------------- */}
      <Section className="border-b border-hull-800">
        <Eyebrow>Workflow</Eyebrow>
        <Heading className="text-white">From signup to signed-off report</Heading>

        <ol className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {WORKFLOW.map((step, index) => (
            <li
              key={step}
              className="flex items-center gap-3 rounded-card border border-hull-700 bg-hull-800/40 px-4 py-3.5"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-hull-700 text-xs font-semibold text-surge-300">
                {index + 1}
              </span>
              <span className="text-sm text-hull-200">{step}</span>
            </li>
          ))}
        </ol>
      </Section>

      {/* --- Security ---------------------------------------------------- */}
      <Section className="border-b border-hull-800">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:gap-16">
          <div>
            <Eyebrow>Security</Eyebrow>
            <Heading className="text-white">Built for teams who ask hard questions</Heading>
            <Lead className="mt-5">
              An AI agent that can drive a browser and read your specifications is a
              powerful thing to hand a codebase. WaveQA treats it as an untrusted
              execution planner and puts explicit boundaries around it.
            </Lead>
          </div>

          <ul className="grid gap-2.5 sm:grid-cols-2">
            {SECURITY.map((item) => (
              <li
                key={item}
                className="flex items-start gap-2.5 rounded-lg border border-hull-800 bg-hull-900/60 px-3.5 py-2.5 text-sm text-hull-200"
              >
                <span aria-hidden="true" className="mt-0.5 text-surge-400">
                  ✓
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* --- CTA --------------------------------------------------------- */}
      <Section>
        <Card className="bg-gradient-to-br from-hull-800 to-hull-900 text-center">
          <Heading className="text-white">Start Building a More Reliable QA Process</Heading>
          <Lead className="mx-auto mt-4 max-w-2xl">
            Create an account, install the agent, and run your first verification against
            your own acceptance criteria.
          </Lead>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <ButtonLink to="/register">Get Started</ButtonLink>
            <ButtonLink to="/pricing" variant="secondary">
              See Pricing
            </ButtonLink>
          </div>
        </Card>
      </Section>
    </>
  );
}

function VerdictRow({
  id,
  status,
  detail,
}: {
  id: string;
  status: 'PASS' | 'FAIL' | 'BLOCKED';
  detail: string;
}) {
  const tones = {
    PASS: 'text-pass-400',
    FAIL: 'text-fail-400',
    BLOCKED: 'text-warn-400',
  } as const;

  return (
    <li className="flex items-baseline gap-3">
      <span className="w-12 shrink-0 text-hull-400">{id}</span>
      <span className={`w-24 shrink-0 font-semibold ${tones[status]}`}>{status}</span>
      <span className="truncate text-xs text-hull-400">{detail}</span>
    </li>
  );
}
